import { supabase } from '@/integrations/supabase/client';

// Interruptor de la herramienta de feedback de pantalla.
// Solo la cuenta Estudio de prueba. UUID en el bundle, como beta-huella.ts.
// El POST va a /api/estudio-feedback; el navegador no habla con Slack.

const USER_PRUEBA = '05eb648c-31b8-4c7b-b971-a93c87033916';
const ORG_PRUEBA = '6ffa8bd9-df15-4879-bf46-48c7449cc8e9';

const NOMBRE_KEY = 'pelip.estudio-feedback.nombre';
const NOTAS_KEY = 'pelip.estudio-feedback.notas.';
const CONSOLA_MAX = 30;
const HTML_TOPE = 8000;
const PADRE_TOPE = 2500;

export function veElFeedbackEstudio(
  userId: string | null | undefined,
  orgId: string | null | undefined,
): boolean {
  if (!userId || !orgId) return false;
  return userId === USER_PRUEBA && orgId === ORG_PRUEBA;
}

export function leerNombreReportero(): string {
  try {
    return (localStorage.getItem(NOMBRE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function guardarNombreReportero(nombre: string) {
  try {
    localStorage.setItem(NOMBRE_KEY, nombre.trim());
  } catch { /* private mode */ }
}

export type LineaConsola = {
  t: string;
  nivel: 'error' | 'warn';
  msg: string;
};

type Anillo = {
  lineas: LineaConsola[];
  restaurar: () => void;
};

function recortarArgs(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return a.message;
      try { return JSON.stringify(a); } catch { return String(a); }
    })
    .join(' ')
    .slice(0, 500);
}

/** Anillo de consola. Solo se instala si el gate dejó pasar. Al desmontar, se restaura. */
export function instalarAnilloConsola(): Anillo {
  const lineas: LineaConsola[] = [];
  const push = (nivel: LineaConsola['nivel'], msg: string) => {
    lineas.push({ t: new Date().toISOString(), nivel, msg });
    if (lineas.length > CONSOLA_MAX) lineas.shift();
  };

  const errorOrig = console.error;
  const warnOrig = console.warn;
  console.error = (...args: unknown[]) => {
    push('error', recortarArgs(args));
    errorOrig.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    push('warn', recortarArgs(args));
    warnOrig.apply(console, args);
  };

  const onError = (ev: ErrorEvent) => {
    push('error', ev.message || 'window.error');
  };
  const onRechazo = (ev: PromiseRejectionEvent) => {
    const r = ev.reason;
    push('error', r instanceof Error ? r.message : String(r));
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRechazo);

  return {
    lineas,
    restaurar: () => {
      console.error = errorOrig;
      console.warn = warnOrig;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRechazo);
    },
  };
}

export function selectorDe(el: Element | null): string {
  if (!el || el === document.documentElement) return 'html';
  const partes: string[] = [];
  let n: Element | null = el;
  while (n && partes.length < 8) {
    if (n.id) {
      partes.unshift(`#${n.id.replace(/([^\w-])/g, '\\$1')}`);
      break;
    }
    let s = n.tagName.toLowerCase();
    const padre = n.parentElement;
    if (padre) {
      const i = [...padre.children].indexOf(n) + 1;
      if (padre.children.length > 1) s += `:nth-child(${i})`;
    }
    partes.unshift(s);
    if (n === document.body) break;
    n = n.parentElement;
  }
  return partes.join(' > ');
}

function htmlSinClaves(el: Element, tope: number): string {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('input[type="password"], input[type="hidden"]').forEach((n) => {
    (n as HTMLInputElement).value = '';
    n.removeAttribute('value');
  });
  let html = clone.outerHTML || '';
  if (html.length > tope) html = html.slice(0, tope) + '…';
  return html;
}

export type SitioClicado = {
  x: number;
  y: number;
  selector: string;
  tag: string;
  id: string;
  className: string;
  texto: string;
  html: string;
  padres: string[];
};

export function describirSitio(el: Element | null, x: number, y: number): SitioClicado {
  const padres: string[] = [];
  let p = el?.parentElement ?? null;
  for (let i = 0; i < 3 && p; i++) {
    padres.push(htmlSinClaves(p, PADRE_TOPE));
    p = p.parentElement;
  }
  const texto = (el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 240);
  return {
    x: Math.round(x),
    y: Math.round(y),
    selector: selectorDe(el),
    tag: el?.tagName.toLowerCase() || '',
    id: (el as HTMLElement | null)?.id || '',
    className: typeof (el as HTMLElement | null)?.className === 'string'
      ? ((el as HTMLElement).className).slice(0, 200)
      : '',
    texto,
    html: el ? htmlSinClaves(el, HTML_TOPE) : '',
    padres,
  };
}

export type FichaPantalla = {
  reportero: string;
  mensaje: string;
  hora: string;
  href: string;
  pathname: string;
  search: string;
  hash: string;
  job_id: string | null;
  file_name: string | null;
  viewport: { w: number; h: number; dpr: number };
  userAgent: string;
  sitio: SitioClicado | null;
  consola: LineaConsola[];
  scroll: {
    window: { x: number; y: number };
    cadena: { selector: string; scroll_top: number; scroll_left: number }[];
  };
  puntos: { u: number; v: number }[];
  color: string;
};

function recogerScroll(desde: Element | null) {
  const cadena: { selector: string; scroll_top: number; scroll_left: number }[] = [];
  let n: Element | null = desde;
  while (n && n !== document.documentElement) {
    if (n.scrollTop || n.scrollLeft) {
      cadena.push({
        selector: selectorDe(n),
        scroll_top: Math.round(n.scrollTop),
        scroll_left: Math.round(n.scrollLeft),
      });
    }
    n = n.parentElement;
  }
  return {
    window: { x: Math.round(window.scrollX), y: Math.round(window.scrollY) },
    cadena,
  };
}

export function recogerFicha(opts: {
  reportero: string;
  mensaje: string;
  jobId: string | null;
  fileName: string | null;
  sitio: SitioClicado | null;
  consola: LineaConsola[];
  puntos: { u: number; v: number }[];
  color: string;
  ancla: Element | null;
}): FichaPantalla {
  return {
    reportero: opts.reportero,
    mensaje: opts.mensaje,
    hora: new Date().toISOString(),
    href: location.href,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    job_id: opts.jobId,
    file_name: opts.fileName,
    viewport: {
      w: window.innerWidth,
      h: window.innerHeight,
      dpr: window.devicePixelRatio || 1,
    },
    userAgent: navigator.userAgent,
    sitio: opts.sitio,
    consola: opts.consola.slice(-CONSOLA_MAX),
    scroll: recogerScroll(opts.ancla),
    puntos: opts.puntos,
    color: opts.color,
  };
}

export type EnvioRespuesta = {
  via: 'slack';
};

export async function enviarInforme(ficha: FichaPantalla, png: Blob): Promise<EnvioRespuesta> {
  const buf = await png.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const png_b64 = btoa(bin);

  const { data: sesion } = await supabase.auth.getSession();
  const token = sesion?.session?.access_token;
  if (!token) throw new Error('Sin sesión');

  const r = await fetch('/api/estudio-feedback', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ficha, png_b64 }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || d?.error) throw new Error(d?.error || `HTTP ${r.status}`);
  return { via: 'slack' };
}

// Nota pegada al tapiz: u,v son fracción del ancla, no píxeles de ventana.
export type NotaPantalla = {
  id: string;
  user_id: string;
  pathname: string;
  search: string;
  selector: string;
  puntos: { u: number; v: number }[];
  comentario: string;
  color: string;
  created_at: string;
};

function notaDeBruto(raw: Record<string, unknown>): NotaPantalla | null {
  const id = String(raw.id || '');
  if (!id) return null;
  const puntosBruto = raw.puntos ?? raw.points;
  return {
    id,
    user_id: String(raw.user_id ?? raw.userId ?? ''),
    pathname: String(raw.pathname || ''),
    search: String(raw.search || ''),
    selector: String(raw.selector || ''),
    puntos: Array.isArray(puntosBruto) ? puntosBruto as { u: number; v: number }[] : [],
    comentario: String(raw.comentario ?? raw.comment ?? ''),
    color: String(raw.color || '#f97316'),
    created_at: String(raw.created_at ?? raw.createdAt ?? ''),
  };
}

export async function cargarNotas(userId: string): Promise<NotaPantalla[]> {
  try {
    const raw = localStorage.getItem(NOTAS_KEY + userId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((m) => (m && typeof m === 'object' ? notaDeBruto(m as Record<string, unknown>) : null))
      .filter((n: NotaPantalla | null): n is NotaPantalla => !!n);
  } catch {
    return [];
  }
}

export async function guardarNotas(userId: string, notas: NotaPantalla[]): Promise<void> {
  try {
    localStorage.setItem(NOTAS_KEY + userId, JSON.stringify(notas));
  } catch {
    throw new Error('No se pudieron guardar las notas');
  }
}
